import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useParams, useNavigate } from '@tanstack/react-router';
import {
  useDeleteEntity,
  useEntity,
  useUpdateEntity,
  type EntityDto,
  type EntityImageDto,
} from '../api/queries/entities';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { EntityDescriptorsEditor } from '../components/entities/EntityDescriptorsEditor';
import { EntityImageGallery } from '../components/entities/EntityImageGallery';
import { EntityLoraPanel } from '../components/entities/EntityLoraPanel';
import { ConsistencyWarningsPanel } from '../components/consistency/ConsistencyWarningsPanel';
import { VoiceProfileTab } from '../components/entities/VoiceProfileTab';
import { RelationshipsTab } from '../components/entities/RelationshipsTab';
import { GenerationModal } from '../components/ai/GenerationModal';
import { AppImage } from '../components/common/AppImage';

type TabValue =
  | 'overview'
  | 'descriptors'
  | 'images'
  | 'lora'
  | 'consistency'
  | 'voice'
  | 'relationships';

export function EntityDetailRoute() {
  const { entityId } = useParams({ strict: false }) as { entityId: string };
  const navigate = useNavigate();
  const entity = useEntity(entityId);
  const update = useUpdateEntity();
  const del = useDeleteEntity();
  const [tab, setTab] = useState<TabValue>('overview');
  const [generationMode, setGenerationMode] = useState<
    'image' | 'audio' | 'text' | null
  >(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  if (entity.isLoading) return <CircularProgress />;
  if (entity.isError || !entity.data)
    return <Typography>Could not load entity.</Typography>;

  const e = entity.data;
  const isCharacter = e.entityType === 'character';

  // Build seed prompt from entity descriptors for portrait generation
  const descriptorSeed = e.descriptors
    .map((d) => `${d.category}: ${d.content}`)
    .join('. ');

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h4" sx={{ flex: 1 }}>
          {e.name}
        </Typography>
        <Chip label={e.entityType} />
        {e.hasApprovedLora && <Chip color="success" label="LoRA approved" />}
        {e.libraryScope === 'shared' && <Chip color="info" label="shared" />}
        <Button color="error" onClick={() => setDeleteConfirmOpen(true)}>
          Delete
        </Button>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Overview" value="overview" />
        <Tab
          label={`Descriptors (${e.descriptors.length})`}
          value="descriptors"
        />
        <Tab label={`Images (${e.images.length})`} value="images" />
        <Tab label="LoRA Models" value="lora" />
        <Tab label="Consistency" value="consistency" />
        {isCharacter && <Tab label="Voice" value="voice" />}
        <Tab label="Relationships" value="relationships" />
      </Tabs>

      {tab === 'overview' && (
        <OverviewTab
          entity={e}
          onSave={async (name, description) => {
            await update.mutateAsync({ id: e.id, name, description });
          }}
          onGeneratePortrait={() => setGenerationMode('image')}
        />
      )}
      {tab === 'descriptors' && (
        <EntityDescriptorsEditor
          entityId={e.id}
          entityType={e.entityType}
          descriptors={e.descriptors}
          onRequestAIGenerate={() => setGenerationMode('text')}
        />
      )}
      {tab === 'images' && (
        <EntityImageGallery
          entityId={e.id}
          images={e.images}
          onRequestGenerate={() => setGenerationMode('image')}
        />
      )}
      {tab === 'lora' && <EntityLoraPanel entity={e} />}
      {tab === 'consistency' && <ConsistencyWarningsPanel entityId={e.id} />}
      {tab === 'voice' && isCharacter && (
        <VoiceProfileTab
          entityId={e.id}
          voiceProfile={
            (e as EntityDto & { voiceProfile?: Record<string, unknown> | null })
              .voiceProfile ?? null
          }
        />
      )}
      {tab === 'relationships' && (
        <Stack spacing={3}>
          <RelationshipsTab entityId={e.id} entityName={e.name} />
          <Box>
            <Typography variant="subtitle2" sx={{ mt: 3 }}>
              World Elements
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Link this entity to world elements in the World Builder.
            </Typography>
          </Box>
        </Stack>
      )}

      <GenerationModal
        open={generationMode !== null}
        onClose={() => setGenerationMode(null)}
        mode={generationMode ?? 'image'}
        seedPrompt={descriptorSeed || `Portrait of ${e.name}`}
        entityId={e.id}
        defaultVoiceId={
          (e.voiceProfile as Record<string, unknown> | null)?.voiceId as
            | string
            | undefined
        }
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete entity"
        message={`Soft-delete this entity "${e.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={async () => {
          await del.mutateAsync(e.id);
          setDeleteConfirmOpen(false);
          navigate({ to: '/entities' });
        }}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </Stack>
  );
}

function OverviewTab({
  entity,
  onSave,
  onGeneratePortrait,
}: {
  entity: EntityDto;
  onSave: (name: string, description: string) => Promise<void>;
  onGeneratePortrait: () => void;
}) {
  const [name, setName] = useState(entity.name);
  const [description, setDescription] = useState(entity.description ?? '');
  const [saving, setSaving] = useState(false);

  const referenceImage: EntityImageDto | undefined = entity.images.find(
    (i) => i.isReference,
  );

  return (
    <Stack spacing={2} sx={{ maxWidth: 800 }}>
      {referenceImage && (
        <AppImage
          src={referenceImage.imageUrl ?? referenceImage.thumbnailUrl}
          width="100%"
          height={300}
          objectFit="cover"
          objectPosition="top center"
          sx={{ borderRadius: 2, mb: 2 }}
        />
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Chip
          size="small"
          variant="outlined"
          label={`${entity.descriptors.length} descriptor${entity.descriptors.length === 1 ? '' : 's'}`}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`${entity.images.length} image${entity.images.length === 1 ? '' : 's'}`}
        />
        <Chip
          size="small"
          variant="outlined"
          color={entity.hasApprovedLora ? 'success' : 'default'}
          label={`LoRA: ${entity.hasApprovedLora ? 'Approved' : 'None'}`}
        />
      </Stack>

      <Box>
        <Button
          variant="outlined"
          startIcon={<AutoFixHighIcon />}
          onClick={onGeneratePortrait}
        >
          Generate Portrait
        </Button>
      </Box>

      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <TextField
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        multiline
        minRows={4}
      />
      <Box>
        <Button
          variant="contained"
          disabled={!name.trim() || saving}
          onClick={async () => {
            setSaving(true);
            try {
              await onSave(name.trim(), description);
            } finally {
              setSaving(false);
            }
          }}
        >
          Save
        </Button>
      </Box>
    </Stack>
  );
}
